// Carte de suivi client — Leaflet + fond vectoriel OpenFreeMap « Bright » (look clair
// type navigation, données OSM). Le moteur de rendu (MapLibre) est chargé depuis le CDN
// officiel (fichier vendor pré-minifié) car NOTRE minifieur de build casse ses workers
// WebGL (canvas blanc en prod — vérifié). Repli raster OSM automatique si le CDN ou le
// rendu échoue : la carte ne peut pas rester vide.
import { useEffect, useRef, useState } from "react";
import { Crosshair, Maximize2, X } from "lucide-react";
import "leaflet/dist/leaflet.css";

const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/bright";
const RASTER_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const MAPLIBRE_JS = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js";
const MAPLIBRE_CSS = "https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css";
const PLUGIN_JS = "https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.1.4/leaflet-maplibre-gl.js";
// mention courte et discrète (obligatoire côté licence OSM, mais compacte)
const MAP_ATTR = '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OSM</a> · <a href="https://www.maptiler.com/copyright/" target="_blank">OpenMapTiles</a> · <a href="https://openfreemap.org" target="_blank">OpenFreeMap</a>';

const BLUE = "#2457FF";
const NAVY = "#0B1F4D";
const GREY = "#98A2B3";

const toLatLngs = (geo) => (geo?.coordinates || []).map(([lon, lat]) => [lat, lon]);

function nearestIndex(latlngs, [lat, lon]) {
  let best = 0;
  let bestDist = Infinity;
  latlngs.forEach(([la, lo], i) => {
    const d = (la - lat) ** 2 + (lo - lon) ** 2;
    if (d < bestDist) { bestDist = d; best = i; }
  });
  return best;
}

const PIN_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:12px;height:12px"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>';
const CAR_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:19px;height:19px"><path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.6 5H8.4a2 2 0 0 0-1.9 1.3L5 10l-2-2"/><path d="M7 14h.01M17 14h.01"/><rect width="18" height="8" x="3" y="10" rx="2"/><path d="M5 18v2M19 18v2"/></svg>';

function pinIcon(L, color, shadow) {
  return L.divIcon({
    className: "miles-pin",
    html: `<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:9999px;background:${color};color:#fff;border:2.5px solid #fff;box-shadow:${shadow}">${PIN_SVG}</span>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

function driverIcon(L, etaMinutes, staleText) {
  const stale = !etaMinutes && staleText;
  const bubble = etaMinutes
    ? `<span data-testid="map-driver-eta" style="position:absolute;left:50%;bottom:calc(100% + 8px);transform:translateX(-50%);white-space:nowrap;background:${NAVY};color:#fff;font-size:11px;font-weight:700;padding:4px 10px;border-radius:9999px;box-shadow:0 8px 20px rgba(11,31,77,.35)">~${etaMinutes} min</span>`
    : stale
      ? `<span data-testid="map-driver-stale" style="position:absolute;left:50%;bottom:calc(100% + 8px);transform:translateX(-50%);white-space:nowrap;background:#475569;color:#fff;font-size:10px;font-weight:600;padding:4px 10px;border-radius:9999px;box-shadow:0 6px 16px rgba(71,85,105,.3)">${staleText}</span>`
      : "";
  // position ancienne : voiture grisée (visible mais distinguée du direct)
  const bg = stale ? "#64748B" : BLUE;
  const ping = stale ? "" : `<span style="position:absolute;inset:-10px;border-radius:9999px;background:rgba(36,87,255,.22);animation:milesPing 1.8s cubic-bezier(0,0,.2,1) infinite"></span>`;
  return L.divIcon({
    className: "miles-driver-marker",
    html: `<span style="position:relative;display:block;width:38px;height:38px">${bubble}${ping}<span style="position:relative;display:flex;align-items:center;justify-content:center;width:38px;height:38px;border-radius:9999px;background:${bg};color:#fff;border:2.5px solid #fff;box-shadow:0 10px 24px rgba(36,87,255,.45)">${CAR_SVG}</span></span>`,
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });
}

function chipTooltip(L, label, direction) {
  return L.tooltip({
    permanent: true,
    direction,
    offset: direction === "top" ? [0, -16] : direction === "bottom" ? [0, 16] : direction === "left" ? [-16, 0] : [16, 0],
    className: "miles-map-chip",
    interactive: false,
  }).setContent(label);
}

export default function LiveRouteMap({ geometry, center, driverLocation, driverRoute, status, etaMinutes, driverLocationAge, staleLabel, fromLabel, toLabel, height = "h-80 sm:h-96", testId = "tracking-live-map" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const groupRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const fittedRef = useRef(false);
  const focusRef = useRef(null); // { driverLatLng, bounds } — cible du bouton focus
  const [mapLibs, setMapLibs] = useState(null); // { L, vector }
  const [rasterMode, setRasterMode] = useState(false);
  const [expanded, setExpanded] = useState(false); // plein écran adaptatif
  const L = mapLibs?.L ?? null;

  const hasData = Boolean((geometry?.coordinates?.length >= 2) || driverLocation || center);

  // chargement paresseux : Leaflet (bundle) + MapLibre depuis le CDN officiel
  // (vendor pré-minifié, épargné par notre pipeline — sinon canvas blanc en prod)
  useEffect(() => {
    let alive = true;
    const loadScript = (src) => new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    (async () => {
      const mod = await import("leaflet");
      const Lx = mod.default ?? mod;
      let vector = false;
      try {
        window.L = Lx; // le plugin UDM attend les globales L et maplibregl
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = MAPLIBRE_CSS;
        document.head.appendChild(link);
        await loadScript(MAPLIBRE_JS);
        await loadScript(PLUGIN_JS);
        vector = typeof Lx.maplibreGL === "function";
      } catch { vector = false; }
      if (alive) setMapLibs({ L: Lx, vector });
    })().catch(() => {});
    return () => { alive = false; };
  }, []);

  // initialisation de la carte une fois les libs dispo ET le conteneur rendu
  // (les données arrivent en asynchrone : hasData passe à true après le 1er rendu)
  useEffect(() => {
    if (!L || !hasData || !containerRef.current || mapRef.current) return undefined;
    const map = L.map(containerRef.current, {
      scrollWheelZoom: true, // molette + trackpad : zoom direct demandé
      zoomControl: false,
      attributionControl: false,
      zoomSnap: 0.5,
      zoomDelta: 0.5,
    });
    // attribution compacte, sans le préfixe « Leaflet »
    L.control.attribution({ prefix: false, position: "bottomleft" }).addTo(map);
    // zoom en pilule arrondie, en haut à droite
    L.control.zoom({ position: "topright" }).addTo(map);
    // repli raster : tuiles <img> incassables
    const addRaster = () => {
      setRasterMode(true);
      try { L.tileLayer(RASTER_URL, { maxZoom: 19, attribution: MAP_ATTR }).addTo(map); } catch { /* noop */ }
    };
    if (mapLibs.vector) {
      try {
        const glLayer = L.maplibreGL({ style: MAP_STYLE_URL, attributionControl: { customAttribution: MAP_ATTR }, interactive: false });
        glLayer.addTo(map);
        // Leaflet diffère l'init de la couche tant que la carte n'a pas de vue :
        // le chien de garde démarre une fois la vue prête (fitBounds juste après)
        map.whenReady(() => {
          const glMap = glLayer.getMaplibreMap?.();
          if (!glMap) { addRaster(); return; }
          let painted = false;
          let fellBack = false;
          const fallback = () => {
            if (painted || fellBack) return;
            fellBack = true;
            try { map.removeLayer(glLayer); } catch { /* noop */ }
            addRaster();
          };
          glMap.once("idle", () => { painted = true; });
          glMap.once("load", () => { setTimeout(fallback, 5000); });
          setTimeout(fallback, 20000); // style jamais chargé (CDN/tuiles injoignables)
        });
      } catch {
        addRaster();
      }
    } else {
      addRaster();
    }
    groupRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; driverMarkerRef.current = null; fittedRef.current = false; };
  }, [mapLibs, hasData]);

  // (re)dessin des couches à chaque mise à jour des données
  useEffect(() => {
    const map = mapRef.current;
    const group = groupRef.current;
    if (!L || !map || !group || !hasData) return;
    group.clearLayers();

    const routeLatLngs = toLatLngs(geometry);
    const legLatLngs = toLatLngs(driverRoute);
    const driverLatLng = driverLocation ? [driverLocation.lat, driverLocation.lon] : null;
    const bounds = [];
    // cadrage : route complète + trajet chauffeur + position chauffeur
    routeLatLngs.forEach((p) => bounds.push(p));
    legLatLngs.forEach((p) => bounds.push(p));
    if (driverLatLng) bounds.push(driverLatLng);
    if (!bounds.length && center) bounds.push([center.lat, center.lon]);
    focusRef.current = { driverLatLng, bounds };

    const needFit = !fittedRef.current
      || (driverLatLng && !map.getBounds().pad(0.25).contains(driverLatLng));
    if (bounds.length && needFit) {
      if (bounds.length === 1) map.setView(bounds[0], 14);
      else map.fitBounds(L.latLngBounds(bounds), { padding: [60, 60] });
      fittedRef.current = true;
    }

    const start = routeLatLngs.length ? routeLatLngs[0] : (center ? [center.lat, center.lon] : null);
    const end = routeLatLngs.length >= 2 ? routeLatLngs[routeLatLngs.length - 1] : null;

    // trajet chauffeur → client (vraie route OSRM, pointillés animés épais style navigation)
    // affiché uniquement avant l'embarquement : une fois le client à bord,
    // c'est le trajet prise en charge → destination qui est la star
    const onBoard = status === "on_board";
    if (!onBoard && legLatLngs.length >= 2) {
      L.polyline(legLatLngs, { color: "#FFFFFF", weight: 7, opacity: 0.85, lineCap: "round", lineJoin: "round" }).addTo(group);
      L.polyline(legLatLngs, { color: NAVY, weight: 3.5, opacity: 0.95, lineCap: "round", lineJoin: "round", className: "miles-driver-leg" }).addTo(group);
    }

    // trajet réservé (prise en charge → destination)
    if (routeLatLngs.length >= 2) {
      L.polyline(routeLatLngs, { color: "#FFFFFF", weight: 7, opacity: 0.9, lineCap: "round", lineJoin: "round" }).addTo(group);
      L.polyline(routeLatLngs, { color: BLUE, weight: 3.5, opacity: 0.95, lineCap: "round", lineJoin: "round" }).addTo(group);
      // client à bord : portion déjà parcourue grisée, le reste reste bleu
      if (onBoard && driverLatLng) {
        const split = nearestIndex(routeLatLngs, driverLatLng);
        if (split > 0) {
          L.polyline(routeLatLngs.slice(0, split + 1), { color: GREY, weight: 3.5, opacity: 0.95, lineCap: "round", lineJoin: "round" }).addTo(group);
        }
      }
    }

    // marqueurs départ / arrivée + étiquettes (latérales si les pins sont proches)
    if (start) {
      const m = L.marker(start, { icon: pinIcon(L, BLUE, "0 4px 12px rgba(36,87,255,.4)"), keyboard: false }).addTo(group);
      if (fromLabel) {
        let dir = "top";
        if (end) {
          const a = map.latLngToContainerPoint(start);
          const b = map.latLngToContainerPoint(end);
          if (a.distanceTo(b) < 90) dir = start[1] <= end[1] ? "left" : "right";
        }
        m.bindTooltip(chipTooltip(L, fromLabel, dir));
      }
    }
    if (end) {
      const m = L.marker(end, { icon: pinIcon(L, NAVY, "0 4px 10px rgba(11,31,77,.35)"), keyboard: false }).addTo(group);
      if (toLabel) {
        let dir = "bottom";
        const a = map.latLngToContainerPoint(start);
        const b = map.latLngToContainerPoint(end);
        if (a.distanceTo(b) < 90) dir = end[1] > start[1] ? "right" : "left";
        m.bindTooltip(chipTooltip(L, toLabel, dir));
      }
    }

    // marqueur chauffeur persistant : déplacement fluide entre deux relevés
    if (driverLatLng) {
      const staleText = !etaMinutes && driverLocationAge > 120 && staleLabel
        ? staleLabel.replace("{min}", Math.max(1, Math.round(driverLocationAge / 60)))
        : null;
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setLatLng(driverLatLng);
        driverMarkerRef.current.setIcon(driverIcon(L, etaMinutes, staleText));
      } else {
        driverMarkerRef.current = L.marker(driverLatLng, {
          icon: driverIcon(L, etaMinutes, staleText),
          zIndexOffset: 1000,
          keyboard: false,
        }).addTo(map);
      }
      driverMarkerRef.current.getElement()?.setAttribute("data-testid", "tracking-driver-marker");
    } else if (driverMarkerRef.current) {
      driverMarkerRef.current.remove();
      driverMarkerRef.current = null;
    }
  }, [L, geometry, center, driverLocation, driverRoute, status, etaMinutes, driverLocationAge, staleLabel, fromLabel, toLabel, hasData]);

  // bouton focus : rezomme sur le véhicule (ou recadre le trajet si pas de position)
  const focusDriver = () => {
    const map = mapRef.current;
    const f = focusRef.current;
    if (!map || !L || !f) return;
    if (f.driverLatLng) {
      map.flyTo(f.driverLatLng, Math.max(map.getZoom(), 14), { duration: 0.6 });
    } else if (f.bounds.length > 1) {
      map.fitBounds(L.latLngBounds(f.bounds), { padding: [44, 44] });
    } else if (f.bounds.length === 1) {
      map.flyTo(f.bounds[0], 14, { duration: 0.6 });
    }
  };

  // plein écran : la carte s'adapte à l'écran (recalcul taille + recadrage du trajet)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !L) return undefined;
    const t = setTimeout(() => {
      map.invalidateSize();
      const f = focusRef.current;
      if (f?.bounds?.length > 1) map.fitBounds(L.latLngBounds(f.bounds), { padding: [48, 48] });
    }, 90);
    return () => clearTimeout(t);
  }, [expanded, L]);

  // plein écran : verrouille le défilement de la page + fermeture via Échap
  useEffect(() => {
    if (!expanded) return undefined;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [expanded]);

  if (!hasData) {
    return (
      <div className={`flex ${height} items-center justify-center bg-cloud`} data-testid={testId}>
        <p className="text-sm text-slate">Carte du trajet indisponible</p>
      </div>
    );
  }

  return (
    <div
      className={expanded
        ? "miles-live-map fixed inset-0 z-[2000] bg-[#EDF1F6]"
        // isolate + z-0 : contexte d'empilement propre — les couches Leaflet (z jusqu'à 1000)
        // ne doivent JAMAIS passer par-dessus le header/hero au scroll mobile
        : `miles-live-map relative isolate z-0 ${height} w-full overflow-hidden bg-[#EDF1F6] ${rasterMode ? "raster-mode" : ""}`}
      data-testid={testId}
      data-expanded={expanded || undefined}
    >
      <div ref={containerRef} className="absolute inset-0" />
      {/* bas droite : agrandir/réduire + focus véhicule (zoom = pilule en haut à droite) */}
      <div className="absolute bottom-3 right-3 z-[1000] flex flex-col items-center gap-2">
        {expanded ? (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            data-testid="map-close-button"
            aria-label="Réduire la carte"
            title="Réduire la carte"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 bg-white text-ink shadow-lg transition-colors duration-200 hover:text-miles"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            data-testid="map-expand-button"
            aria-label="Agrandir la carte"
            title="Agrandir la carte"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 bg-white text-ink shadow-lg transition-colors duration-200 hover:text-miles"
          >
            <Maximize2 className="h-4 w-4" strokeWidth={2} />
          </button>
        )}
        <button
          type="button"
          onClick={focusDriver}
          data-testid="map-focus-button"
          aria-label="Recentrer sur le véhicule"
          title="Recentrer sur le véhicule"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 bg-white text-ink shadow-lg transition-colors duration-200 hover:text-miles"
        >
          <Crosshair className="h-5 w-5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
